import type { ConditionalProperty } from 'Automations/nodeDefinitions/conditions'
import {
  quoteTemplateAttachments,
  quoteTemplatePaymentTerms,
  quoteTemplatePricing,
  quoteTemplateRawText
} from 'Automations/nodeDefinitions/conditions/helpers/functionStrings'
import { semanticTypes } from 'Automations/nodeDefinitions/semanticTypes'
import {
  formatQuoteComparison,
  formatQuoteTemplateName,
  formatQuotePricingSubOperator,
  formatQuotePricingSubject
} from '../../../../switch/components/GroupEditor/inputs'
import { QuoteTemplatePropertyEditor } from './QuoteTemplatePropertyEditor'
import { quoteTemplateSupportingTask } from './quoteTemplate.supportingTask'
import {
  isQuoteComparisonField,
  ATTACHMENTS_COMPARISON_LABELS,
  PAYMENT_TERMS_COMPARISON_LABELS
} from '../quoteComparison'
import { readRow } from './QuoteTemplatePropertyEditor.helpers'

// Every template field stores the same compound value, so one validator serves
// them all. Each missing piece returns its own `ValidatorReturn` tagged with
// `metadata.field`, which the property editor reads to attach the error to the
// correct sub-input.
const validateQuoteTemplateValue = ({ value }: { value: unknown }) => {
  const { templateId, subOperator } = readRow(value)
  const results = []
  if (!templateId) {
    results.push({
      title: 'Incomplete condition',
      message: 'Template required',
      metadata: { field: 'templateId' as const }
    })
  }
  if (!subOperator) {
    results.push({
      title: 'Incomplete condition',
      message: 'Operator required',
      metadata: { field: 'operator' as const }
    })
  }
  return results.length ? results : undefined
}

export const quoteTemplateConditionalProperty: ConditionalProperty = {
  label: 'Quote template',
  // Surfaces the chosen template's title between the property label and the
  // operator, so each operator's `formatPreviewValue` stays focused on its own
  // comparison (e.g. "attachments are equal").
  formatPreviewProperty: formatQuoteTemplateName,
  // The property editor renders its own template picker and field dropdown, so
  // auto-opening the framework operator dropdown would be wasted UI.
  focusOperatorOnSelect: false,
  propertyEditorComponent: QuoteTemplatePropertyEditor,
  // Always available: a switch can hold multiple QUOTE_TEMPLATE rows, each
  // scoped to a different template or template field.
  propertyAvailable: () => true,
  validateProperty: ({ operator, value }) =>
    isQuoteComparisonField(operator)
      ? validateQuoteTemplateValue({ value })
      : undefined,
  operators: {
    ATTACHMENTS: {
      label: 'Attachments',
      // The quote side of the comparison — genuinely published by the start
      // node, so the usual upstream gating applies. The template side is
      // supplied by `valueSupportingTask` and reached via factory config.
      valueSemanticType: semanticTypes.sequence.quote.attachments,
      functionString: quoteTemplateAttachments({
        templateSemanticType: semanticTypes.sequence.quoteTemplate.attachments
      }),
      // The operator label supplies the template field and the value formatter
      // the comparison, so the label reads "<template> attachments are equal".
      // Both dropdown and preview read the same labels.
      formatPreviewValue: formatQuoteComparison(ATTACHMENTS_COMPARISON_LABELS)
    },
    RAW_TEXT: {
      label: 'Raw text',
      valueSemanticType: semanticTypes.sequence.quote.rawText,
      functionString: quoteTemplateRawText({
        templateSemanticType: semanticTypes.sequence.quoteTemplate.rawText
      }),
      // Singular subject, so the default labels apply: the canvas reads
      // "<template> raw text is equal".
      formatPreviewValue: formatQuoteComparison()
    },
    PAYMENT_TERMS: {
      label: 'Payment terms',
      valueSemanticType: semanticTypes.sequence.quote.paymentTerms,
      functionString: quoteTemplatePaymentTerms({
        templateSemanticType: semanticTypes.sequence.quoteTemplate.paymentTerms
      }),
      // Plural subject, so the canvas reads "<template> payment terms are
      // equal".
      formatPreviewValue: formatQuoteComparison(PAYMENT_TERMS_COMPARISON_LABELS)
    },
    PRICING: {
      label: 'Pricing',
      // A quote's pricing lives entirely in its phases, so this gates the
      // operator on the quote publishing them — which is also what the
      // supporting task feeds the comparison executor as its source.
      valueSemanticType: semanticTypes.sequence.quote.phases,
      functionString: quoteTemplatePricing({
        comparisonSemanticTypes: {
          ALL: semanticTypes.sequence.quotePricingComparison.allMatch,
          PRICES: semanticTypes.sequence.quotePricingComparison.pricesMatch,
          DISCOUNTS:
            semanticTypes.sequence.quotePricingComparison.discountsMatch,
          MINIMUMS: semanticTypes.sequence.quotePricingComparison.minimumsMatch
        }
      }),
      // The chosen comparison is the subject, so it replaces the field in the
      // clause: "<template> prices are equal", "<template> contract terms are
      // equal".
      formatPreviewOperator: formatQuotePricingSubject,
      formatPreviewValue: formatQuotePricingSubOperator
    }
  },
  valueSupportingTask: quoteTemplateSupportingTask
}
